import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft,
  Eye,
  Calendar,
  User,
  FileText,
  Signature,
  Image as ImageIcon,
  Edit,
  Trash2,
  Archive,
  AlertTriangle,
  Info,
  RefreshCw,
  Send,
  BookOpenCheck,
  Copy,
  X as XIcon,
  AlertCircle
} from "lucide-react";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import { Memo } from "../types";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";
import { generateNextMemoNumber } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

const ImageViewer = ({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) => {
  if (!src) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm z-[9997]"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-opacity z-[9999]"
        onClick={onClose}
      >
        <XIcon size={32} />
      </button>
      <div className="relative w-[95vw] h-[95vh] md:w-[90vw] md:h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} className="w-full h-full object-contain rounded-lg shadow-2xl" />
      </div>
    </div>
  );
};

// Local helpers removed, using toSydneyDate and formatTimestamp from helpers

// Utility to sanitize captions
function sanitizeCaption(caption: string) {
  if (!caption) return "";
  return caption;
}

// Utility to truncate title
function truncateTitle(title: string, maxLength: number = 48) {
  if (!title) return "";
  return title.length > maxLength ? title.substring(0, maxLength) + "..." : title;
}

export default function MemoViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    memos,
    getUserName,
    getUserById,
    users,
    memoAssignments,
    fetchMemoAssignments,
    deleteMemo,
    refreshMemos
  } = useData();
  const [memo, setMemo] = useState<Memo | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Add duplicate functionality state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [duplicateVersion, setDuplicateVersion] = useState('1');

  // Archive validation state  
  const [archiveBlockedReason, setArchiveBlockedReason] = useState<string>('');
  const [blockedAssignments, setBlockedAssignments] = useState<any[]>([]);
  const [showArchiveBlockedDialog, setShowArchiveBlockedDialog] = useState(false);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState<string>('');

  // Get memo assignment state
  const getMemoAssignmentState = useMemo(() => {
    if (!memo || !currentUser) return 'none';

    // Find current user's assignment for this memo
    const userAssignment = memoAssignments.find(assignment =>
      assignment.memo?.id == memo.id && assignment.user?.id == currentUser.id
    );

    if (!userAssignment) return 'none';

    // Check completion status
    if (userAssignment.signatureType && userAssignment.signatureData) {
      return 'completed';
    } else if (userAssignment.readDate) {
      return 'active'; // Read but not signed
    } else {
      return 'active'; // Assigned but not read
    }
  }, [memo, currentUser, memoAssignments]);

  // Check if archive is likely to be blocked
  const hasActiveAssignments = useMemo(() => {
    if (!memo) return false;

    return memoAssignments.some(assignment =>
      assignment.memo?.id === memo.id &&
      (!assignment.signatureType || !assignment.signatureData) // Not signed
    );
  }, [memo, memoAssignments]);

  // const isAdmin = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === "HSEQ Manager";

  // Determine if editing is allowed
  const canEdit = useMemo(() => {
    if (!memo) return false;
    if (memo.status !== 'Active') return true;
    return getMemoAssignmentState !== 'active';
  }, [memo, currentUser, getMemoAssignmentState]);

  // Determine if archiving is allowed
  const canArchive = useMemo(() => {
    if (!memo) return false;
    if (memo.status !== 'Active') return false;  // Only allow archiving of Active memos
    return getMemoAssignmentState !== 'active';
  }, [memo, currentUser, getMemoAssignmentState]);

  // Determine if deleting is allowed (stricter rules)
  const canDelete = useMemo(() => {
    if (!memo) return false;

    // Only allow deletion of Draft memos
    // Block deletion if memo has ever been assigned (to preserve audit trail)
    if (memo.status === 'Draft') {
      const currentAssignments = memoAssignments.filter(a => a.memo?.id == memo.id);
      const hasNoAssignmentHistory = currentAssignments.length === 0;
      return hasNoAssignmentHistory;
    }

    // Block deletion for Active and Archived memos (preserve audit trail)
    return false;
  }, [memo, currentUser, memoAssignments]);

  // Banner Components
  const MemoBanner = () => {
    if (!memo) return null;

    if (memo.status === 'Active' && getMemoAssignmentState === 'active') {
      return (
        <Alert className="mb-6 border-yellow-500 bg-yellow-50 text-yellow-900">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>
            You cannot edit this memo because it is currently assigned for signature.
            Please recall the assignment if you need to make changes.
          </AlertDescription>
        </Alert>
      );
    }

    if (memo.status === 'Active' && getMemoAssignmentState === 'completed') {
      const lastCompletedAssignment = memoAssignments
        .filter(a => a.memo?.id == memo.id && a.readDate)
        .sort((a, b) => new Date(b.readDate!).getTime() - new Date(a.readDate!).getTime())[0];

      return (
        <Alert className="mb-6 border-blue-500 bg-blue-50 text-blue-900">
          <Info className="h-5 w-5" />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription>
            This memo has been signed previously under Assignment on{' '}
            {lastCompletedAssignment ? toSydneyDate(lastCompletedAssignment.readDate!) : 'unknown date'}.
            If you edit this memo, you may want to reassign it for a new signature.
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  const getSignedUrl = async (attachmentId: string) => {
    try {
      console.log('Fetching signed URL for attachment:', attachmentId);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${id}/attachments/${attachmentId}/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      console.log('Preview response:', data);

      if (!response.ok) {
        console.error('Preview request failed:', data);
        throw new Error(data.message || 'Failed to get signed URL');
      }

      if (data.success && data.previewUrl) {
        console.log('Setting signed URL for attachment:', attachmentId);
        setSignedUrls(prev => ({ ...prev, [attachmentId]: data.previewUrl }));
      } else {
        console.error('Invalid preview response:', data);
        throw new Error('Invalid preview response');
      }
    } catch (error) {
      console.error('Error getting signed URL:', error);
      toast.error('Failed to load image preview');
    }
  };

  const handleDownload = async (attachmentId: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${id}/attachments/${attachmentId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to get download URL');

      const data = await response.json();
      if (data.success && data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      } else {
        toast.error('Failed to generate download link');
      }
    } catch (error) {
      console.error('Error getting download URL:', error);
      toast.error('Failed to download file');
    }
  };

  // Handle memo archive
  const handleArchiveMemo = async () => {
    if (!memo || !currentUser) return;

    setIsProcessing(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${memo.id}/archive`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: archiveReason })
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Handle specific archive validation errors
        if (response.status === 400 && errorData.activeAssignmentCount) {
          setArchiveBlockedReason(errorData.message);
          setBlockedAssignments(errorData.assignedUsers || []);
          setShowArchiveDialog(false);
          setShowArchiveBlockedDialog(true);
          return;
        }

        throw new Error(errorData.message || 'Failed to archive memo');
      }

      setShowArchiveDialog(false);
      setArchiveReason("");
      toast.success('Memo archived successfully');

      await refreshMemos();

      // Navigate back to memos list
      setTimeout(() => {
        navigate('/policies?tab=memos');
      }, 1500);

    } catch (error: any) {
      console.error('Error archiving memo:', error);

      // Handle cases where error comes through differently
      if (error.message && error.message.includes('active assignments')) {
        setArchiveBlockedReason(error.message);
        setShowArchiveDialog(false);
        setShowArchiveBlockedDialog(true);
      } else {
        toast.error('Failed to archive memo');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle memo delete
  const handleDeleteMemo = async () => {
    if (!memo || !currentUser) return;

    setIsProcessing(true);
    try {
      await deleteMemo(memo.id);
      setShowDeleteDialog(false);
      navigate('/policies?tab=memos');
    } catch (error) {
      console.error('Error deleting memo:', error);
      toast.error('Failed to delete memo');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle memo unarchive
  const handleUnarchiveMemo = async () => {
    if (!memo || !currentUser) return;

    setIsProcessing(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${memo.id}/unarchive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to unarchive memo');

      setShowUnarchiveDialog(false);
      toast.success('Memo unarchived successfully');

      await refreshMemos();

    } catch (error) {
      console.error('Error unarchiving memo:', error);
      toast.error('Failed to unarchive memo');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add duplicate functionality
  const handleDuplicate = async () => {
    if (!memo) return;

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/duplicate/${memo.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: duplicateTitle,
          version: duplicateVersion,
          number: generateNextMemoNumber(memos)
        })
      });

      if (response.ok) {
        const data = await response.json();

        toast.success('Memo duplicated successfully');
        setDuplicateModalOpen(false);
        setDuplicateTitle('');
        setDuplicateVersion('1');
        // Navigate to the new memo
        navigate(`/memos/${data.memo.id}`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to duplicate memo');
      }
    } catch (error) {
      console.error('Error duplicating memo:', error);
      toast.error('Failed to duplicate memo');
    }
  };

  const openDuplicateModal = () => {
    if (memo) {
      setDuplicateTitle(`${memo.subject} (Copy)`);
      setDuplicateVersion('1');
      setDuplicateModalOpen(true);
    }
  };

  // Fetch memo assignments when page loads (for admin view)
  useEffect(() => {
    if (memo && currentUser?.userRole === 'Admin') {
      fetchMemoAssignments(memo.id);
    }
  }, [memo?.id, currentUser?.userRole]); // Functions intentionally omitted from deps to prevent infinite loop

  useEffect(() => {
    const fetchMemo = async () => {
      if (!id) return;

      setLoading(true);
      try {
        // Always fetch fresh data from API to ensure we have complete attachment data
        // This resolves the issue where newly created memos don't show images
        console.log('Fetching memo from API for complete data...');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
        const response = await fetch(`${API_BASE}/api/memos/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            toast.error('Memo not found');
          } else {
            toast.error('Failed to load memo');
          }
          navigate('/policies?tab=memos');
          return;
        }

        const data = await response.json();
        if (data.success && data.memo) {
          console.log('Fetched memo from API:', data.memo);
          setMemo(data.memo);

          // Load signed URLs for all image attachments
          if (data.memo.attachments && data.memo.attachments.length > 0) {
            const imageAttachments = data.memo.attachments.filter(
              (attachment: any) => attachment.mimetype.startsWith('image/')
            );

            console.log('Loading signed URLs for', imageAttachments.length, 'image attachments');

            await Promise.all(
              imageAttachments.map((attachment: any) => getSignedUrl(attachment.id))
            );
          }
        } else {
          console.error('Invalid API response:', data);
          toast.error('Failed to load memo');
          navigate('/policies?tab=memos');
        }
      } catch (error) {
        console.error('Error fetching memo:', error);
        toast.error('Failed to load memo');
        navigate('/policies?tab=memos');
      } finally {
        setLoading(false);
      }
    };

    fetchMemo();
  }, [id, memos, navigate]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-20 bg-gray-200 rounded"></div>
            <div className="h-8 w-64 bg-gray-200 rounded"></div>
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!memo) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="mb-2">Memo Not Found</h3>
            <p className="text-muted-foreground mb-4">
              The memo you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/policies?tab=memos')}>
              Back to Policies & Memos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active': return 'default';
      case 'Draft': return 'secondary';
      case 'Archived': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <>
      <ImageViewer
        src={selectedImage || ''}
        alt={selectedImageAlt}
        onClose={() => setSelectedImage(null)}
      />

      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/policies?tab=memos">
              <Button variant="outline" size="icon" className="flex-shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex flex-col">
              <h1 className="text-xl md:text-2xl font-bold leading-tight">{memo.subject}</h1>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-muted-foreground">{memo.number}</p>
                <Badge variant={getStatusBadgeVariant(memo.status)}>{memo.status}</Badge>
              </div>
            </div>
          </div>

          <Can I={ACTIONS.UPDATE} a={RESOURCES.MEMO}>
            <div className="w-full md:w-auto">
              <div className="grid grid-cols-2 md:flex md:items-center gap-2">
                {memo.status === 'Active' && (
                  <>
                    <Button variant="outline" className="w-full" onClick={openDuplicateModal} disabled={isProcessing}><Copy className="h-4 w-4 mr-2" />Duplicate</Button>
                    <Can I={ACTIONS.ARCHIVE} a={RESOURCES.MEMO}>
                      <Button variant="outline" className="w-full" onClick={() => setShowArchiveDialog(true)} disabled={!canArchive || hasActiveAssignments}><Archive className="h-4 w-4 mr-2" />Archive</Button>
                    </Can>
                  </>
                )}
                {memo.status === 'Draft' && (
                  <>
                    <Button variant="outline" className="w-full" onClick={() => navigate(`/memos/${memo.id}/edit`)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
                    <Can I={ACTIONS.DELETE} a={RESOURCES.MEMO}>
                      <Button variant="destructive" className="w-full" onClick={() => setShowDeleteDialog(true)} disabled={!canDelete}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
                    </Can>
                  </>
                )}
                {memo.status === 'Archived' && (
                  <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.MEMO}>
                    <Button variant="outline" className="w-full col-span-2" onClick={() => setShowUnarchiveDialog(true)} disabled={isProcessing}><RefreshCw className="h-4 w-4 mr-2" />Unarchive</Button>
                  </Can>
                )}
              </div>
            </div>
          </Can>
        </div>

        {/* Banners */}
        <MemoBanner />

        {/* "Active Memo - Edit Restricted" Banner - IMPROVED UI */}
        {memo.status === 'Active' && (
          <Can I={ACTIONS.UPDATE} a={RESOURCES.MEMO}>
            <Card className="bg-background shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Info className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col">
                    <p className="font-semibold">Active Memo - Edit Restricted</p>
                    <p className="text-sm text-muted-foreground">
                      Use "Duplicate" to create a new version if changes are needed.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Can>
        )}

        {/* "Archived" Banner - IMPROVED UI */}
        {memo.status === 'Archived' && (
          <Card className="bg-background shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="flex flex-col">
                  <p className="font-semibold">Archived</p>
                  <p className="text-sm text-muted-foreground">
                    This memo is archived and read-only.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle>Document</CardTitle></CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold border-b pb-2 mb-3">Content</h3>
                  <div className="prose prose-sm md:prose-base max-w-none text-foreground whitespace-pre-wrap">
                    {memo.content}
                  </div>
                </div>
              </CardContent>
            </Card>

            {(memo.attachments && memo.attachments.length > 0) && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5" /> Attachments</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {memo.attachments.map((attachment) => (
                      <div key={attachment.id} className="border rounded-lg overflow-hidden group">
                        <button className="w-full text-left" onClick={() => { setSelectedImage(signedUrls[attachment.id]); setSelectedImageAlt(attachment.caption || attachment.filename); }}>
                          <div className="aspect-video bg-muted flex items-center justify-center">
                            {signedUrls[attachment.id] ? (
                              <img src={signedUrls[attachment.id]} alt={attachment.caption || attachment.filename} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                            ) : (
                              <ImageIcon className="h-8 w-8 text-muted-foreground animate-pulse" />
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-sm font-medium truncate">{attachment.filename}</p>
                            {attachment.caption && <p className="text-xs text-muted-foreground italic truncate">{sanitizeCaption(attachment.caption)}</p>}
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Column (Corrected) */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Memo Information</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-4">
                {/* Section 1: Core Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-muted-foreground">Number</p>
                    <p>{memo.number}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">Status</p>
                    <Badge variant={getStatusBadgeVariant(memo.status)}>{memo.status}</Badge>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Signature Required</p>
                  <p>{memo.requireSignature ? 'Yes' : 'No'}</p>
                </div>

                {/* Section 2: Dates */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <p className="font-medium text-muted-foreground">Effective Date</p>
                    <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {toSydneyDate(memo.effectiveDate)}</p>
                  </div>
                  {memo.expiryDate && (
                    <div>
                      <p className="font-medium text-muted-foreground">Expiry Date</p>
                      <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {toSydneyDate(memo.expiryDate)}</p>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-muted-foreground">Created On</p>
                    <p>{toSydneyDate(memo.createdAt)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">Last Updated</p>
                    <p>{toSydneyDate(memo.updatedAt)}</p>
                  </div>
                </div>

                {/* Section 3: People */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <p className="font-medium text-muted-foreground">Created By</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {(() => {
                        const user = typeof memo.createdBy === 'object' ? memo.createdBy : getUserById(memo.createdBy);
                        return user ? <Link to={`/people/${user.id}`} className="text-primary hover:underline">{user.name}</Link> : "Unknown";
                      })()}
                    </div>
                  </div>
                  {memo.approvedBy && (
                    <div>
                      <p className="font-medium text-muted-foreground">Approved By</p>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {(() => {
                          const user = typeof memo.approvedBy === 'object' ? memo.approvedBy : getUserById(memo.approvedBy);
                          return user ? <Link to={`/people/${user.id}`} className="text-primary hover:underline">{user.name}</Link> : "Unknown";
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Archive Confirmation Dialog */}
        <Dialog open={showArchiveDialog} onOpenChange={(open) => {
          setShowArchiveDialog(open);
          if (!open) setArchiveReason("");
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-blue-500" />
                Archive Memo
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to archive "{memo?.subject}"?
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 mb-2">This action will:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Change the memo status to "Archived"</li>
                  <li>• You can undo this action at any time</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="archiveReason">Reason for archiving (optional)</Label>
                <Textarea
                  id="archiveReason"
                  placeholder="Enter the reason for archiving this memo..."
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  className="resize-none"
                  rows={3}
                />
              </div>

              <div className="flex justify-between gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowArchiveDialog(false);
                    setArchiveReason("");
                  }}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleArchiveMemo}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4" />
                      Archive Memo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Memo
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete "{memo?.subject}"? This action cannot be undone.
              </p>

              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <h4 className="font-medium text-destructive mb-2">Warning:</h4>
                <ul className="text-sm text-destructive/80 space-y-1">
                  <li>• This will permanently delete the memo</li>
                  <li>• All associated data will be removed</li>
                  <li>• This action cannot be reversed</li>
                </ul>
              </div>

              <div className="flex justify-between gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteMemo}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Memo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Unarchive Confirmation Dialog */}
        <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-500" />
                Unarchive Memo
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to unarchive "{memo?.subject}"?
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 mb-2">This action will:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Change the memo status back to "Active"</li>
                  <li>• Make the memo visible to users again</li>
                </ul>
              </div>

              <div className="flex justify-between gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowUnarchiveDialog(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUnarchiveMemo}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                      Unarchiving...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Unarchive Memo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicate Memo Modal */}
        <Dialog open={duplicateModalOpen} onOpenChange={setDuplicateModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Duplicate Memo</DialogTitle>
              <DialogDescription>
                Create a copy of "{memo?.subject}" as a new draft memo.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="duplicate-title" className="text-right">
                  Title
                </Label>
                <Input
                  id="duplicate-title"
                  value={duplicateTitle}
                  onChange={(e) => setDuplicateTitle(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="duplicate-version" className="text-right">
                  Version
                </Label>
                <Input
                  id="duplicate-version"
                  value={duplicateVersion}
                  onChange={(e) => setDuplicateVersion(e.target.value)}
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicateModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDuplicate} disabled={!duplicateTitle.trim()}>
                Duplicate Memo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive Blocked Dialog */}
        <Dialog open={showArchiveBlockedDialog} onOpenChange={setShowArchiveBlockedDialog}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Cannot Archive Memo
              </DialogTitle>
              <DialogDescription>
                This memo cannot be archived because it has active assignments.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                <div className="text-sm text-amber-800">
                  {archiveBlockedReason}
                </div>
              </div>

              {blockedAssignments.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Users with pending assignments:</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {blockedAssignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div>
                          <div className="font-medium">{assignment.name}</div>
                          <div className="text-sm text-gray-600">{assignment.email}</div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            {assignment.status}
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">
                            Assigned: {toSydneyDate(assignment.assignedDate)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
                <div className="text-sm text-blue-800">
                  <strong>Recommendation:</strong> Wait for users to complete their assignments, or manually cancel the assignments before archiving this memo.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArchiveBlockedDialog(false)}>
                Close
              </Button>
              <Button onClick={() => {
                setShowArchiveBlockedDialog(false);
                // Navigate to assignments view to manage them
                navigate('/policies?tab=assignments');
              }}>
                Manage Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
} 